import { Injectable, Injector, Predicate, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  City,
  Country,
  Hotel,
  HotelServiceEnum,
  HotelTypeEnum,
  SearchOnlyHotelPackagesRequest
} from '@appTypes/api.types';
import { SearchDateModes, UnsafeValue } from '@appTypes/common.types';
import { DistrictsTreeItem } from '@appTypes/redefined-types';
import { convertLocationsIntValuesToValues, deepClean, generateNumbersRange } from '@core/misc/misc.utils';
import { greaterThan, lessThan, minDate, minDateThanField, minNumbers } from '@core/misc/validators.utils';
import { RequestStateModel } from '@core/store/store.models';
import { SVGIcon, SVG_ICONS } from '@data/svg-icons.data';
import { UpdateFormValue } from '@ngxs/form-plugin';
import { createDispatchMap, createSelectMap } from '@ngxs/store';
import {
  CHILDRAN_AGES,
  PAGINATION_LEVELS,
  STATIC_OPTIONS_HOTEL_CLASSES_STARS,
  STATIC_OPTIONS_HOTEL_MEAL_PLANS,
  STATIC_OPTONS_HOTEL_SERVICES,
  STATIC_OPTONS_HOTEL_TYPES
} from '@pages/_common/static-data.const';
import { HotelBookingInstructionsComponent } from '@pages/hotels-page/components/hotel-booking-instructions/hotel-booking-instructions.component';
import { NGXS_HOTELS_SEARCH_FORM } from '@pages/hotels-page/hotels-page.const';
import {
  hotelsGetDestinationCountries,
  hotelsGetHotels,
  hotelsGetLocations,
  hotelsResetResult,
  hotelsSearchHotels
} from '@pages/hotels-page/state/hotels.actions';
import { HotelsState } from '@pages/hotels-page/state/hotels.state';
import { LoadingWindowService } from '@shared/components/loading-window/loading-window.service';
import { ConfirmService } from '@shared/modules/confirm/confirm.service';
import { DynamicModalService } from '@shared/modules/dynamic-modal/dynamic-modal.service';
import { pickDestinationCountry } from '@core/misc/destination-country.utils';
import { SEARCH_LINK_ADJUSTED_MESSAGE, clampSearchLinkParty, warnSearchLink } from '@core/misc/search-link.utils';
import { addDays, differenceInDays } from 'date-fns';
import { Observable, combineLatest, filter, lastValueFrom, map, take } from 'rxjs';
import {
  appClearSearchByUrl,
  appResultIsFromGeneratedUrl,
  appSetCurrency,
  appSetSellCurrency
} from '@app-state/app.actions';
import { AppPersistedState } from '../../../state/app.persisted.state';
import { AppState } from '../../../state/app.state';

@Injectable()
export class HotelsSearchFormService {
  //Injectors
  readonly #injector = inject(Injector);
  readonly #loadingWindowService = inject(LoadingWindowService);
  readonly #dynamicModalService = inject(DynamicModalService);
  readonly #confirmService = inject(ConfirmService);

  //Definitions
  readonly today = new Date(new Date().setHours(0, 0, 0, 0));
  readonly searchByPeriodNightsOptions = generateNumbersRange(2, 7);
  readonly ngxsForm = NGXS_HOTELS_SEARCH_FORM;
  readonly pages = PAGINATION_LEVELS;
  readonly childrenAgesData = CHILDRAN_AGES;
  /** Rooms: Auto (0) or 1-5; the label of Auto is translated in the template. */
  readonly roomsData = [0, 1, 2, 3, 4, 5].map((value) => ({ value, label: value === 0 ? 'Auto' : `${value}` }));

  //Selectors
  readonly #selectors = createSelectMap({
    destinationCountries: HotelsState.destinationCountries,
    destinationDefaultCountry: HotelsState.destinationDefaultCountry,
    locations: HotelsState.locations,
    hotels: HotelsState.hotels,
    searchFormData: HotelsState.searchFormData,
    searchByUrlData: AppState.searchByUrlData('hotels'),
    activedDestinationCountry: AppPersistedState.activedDestinationCountry,
    activedCurrency: AppPersistedState.activedCurrency,
    currencies: AppState.currencies,
    isGeneratedURL: AppState.isGeneratedURL,
    formStatus: HotelsState.searchLoadStatus,
    firstBatchReady: HotelsState.searchFirstBatchReady
  });

  //Actions
  readonly actions = createDispatchMap({
    updateFormValue: UpdateFormValue,
    getDestinationCountries: hotelsGetDestinationCountries,
    getLocations: hotelsGetLocations,
    getHotels: hotelsGetHotels,
    resetSearchResult: hotelsResetResult,
    searchHotels: hotelsSearchHotels,
    setCurrency: appSetCurrency,
    setSellCurrency: appSetSellCurrency,
    clearSearchByUrl: appClearSearchByUrl,
    resultIsFromGeneratedUrl: appResultIsFromGeneratedUrl
  });

  //Writable Values
  #isSearchByModule = false;
  #storeCountryCode: string | null = this.#selectors.activedDestinationCountry();
  #isInit = false;
  #isReady = false;

  //Signals
  readonly isSubmited = signal<boolean>(false);
  readonly searchMode = signal<SearchDateModes | null>(null);
  readonly pageRowCount = signal<number>(25);

  //Form Groups
  readonly formGroup = new FormGroup({
    checkInDate: new FormControl<Date>(this.today, [
      Validators.required,
      minDate(this.today, 'Check In date cannot be in the past') // R-HF4 (website-web#32): today is allowed
    ]),
    checkInDateTo: new FormControl<Date | null>(null),
    checkOutDate: new FormControl<Date | null>(null, [
      minDateThanField('checkInDate', '"Check out" Date must be after "Check in" date')
    ]),
    checkInAndCheckOutDaysCount: new FormControl<number | null>(1, [
      Validators.max(30),
      minNumbers(1, `Number of nights must be greater than {minNumbers}`) // R-TX1 (website-web#35): the ru-RU key has single braces
    ]),
    nights: new FormControl<number | null>(null),
    nightsTo: new FormControl<number | null>(null),
    children: new FormControl<number>(0),
    adults: new FormControl<number>(2),
    // multi-room search (docs features/website/multi-room-search.md): 0 = auto (several rooms only where one room cannot hold the party), 1-5 = that many
    rooms: new FormControl<number>(0),
    childrenAges: new FormArray<FormControl<number | null>>([]),
    hotelCodes: new FormControl<string[] | null>(null),

    locations: new FormControl<DistrictsTreeItem[] | null>(null),
    mealPlans: new FormControl<string[] | null>(null),
    countryCode: new FormControl<string | null>(this.#selectors.activedDestinationCountry(), [Validators.required]),
    districtUIDs: new FormControl<string[] | null>(null), //?
    extendedSearch: new FormControl<boolean>(false),

    totalPriceFrom: new FormControl<number | null>(null, [lessThan('totalPriceTo')]),
    totalPriceTo: new FormControl<number | null>(null, [greaterThan('totalPriceFrom')]),
    hotelTypes: new FormControl<HotelTypeEnum[] | null>(null), //HotelTypeEnum[] | null;
    hotelClasses: new FormControl<string[] | null>(null),
    hotelServices: new FormControl<HotelServiceEnum[] | null>(null),
    /*  */
    freeSale: new FormControl<boolean | null>(null),
    groupByHotel: new FormControl<boolean | null>(null),
    searchCurrency: new FormControl<string | null>(null),

    //Int values
    countryId: new FormControl<number | null>(null),
    hotelIds: new FormControl<number[] | null>(null)
  });

  //FormValues (Signals)
  /**
   * R-HP-R1 (website-web#33; multi-room-search.md R-MR7): Rooms = 1 allows 10 adults and 10 children; Auto (0) or 2-5 rooms allow 30 adults and
   * 20 children (the runtime splits up to 10 guests per room). Rooms is undefined until it first changes - that is Auto.
   */
  readonly passengerLimits = computed(() =>
    this.formValues.rooms() === 1 ? { adults: 10, children: 10 } : { adults: 30, children: 20 }
  );

  readonly formValues = {
    adults: toSignal(this.formGroup.controls.adults.valueChanges.pipe(map((e) => e ?? 0))),
    children: toSignal(this.formGroup.controls.children.valueChanges.pipe(map((e) => e ?? 0))),
    rooms: toSignal(this.formGroup.controls.rooms.valueChanges.pipe(map((e) => e ?? 0))),
    checkInDate: toSignal(this.formGroup.controls.checkInDate.valueChanges),
    checkOutDate: toSignal(this.formGroup.controls.checkOutDate.valueChanges),
    countryCode: toSignal(this.formGroup.controls.countryCode.valueChanges),
    mealPlans: toSignal(this.formGroup.controls.mealPlans.valueChanges.pipe(map((e) => e?.join(','))))
  };

  /** R-HP-R2 (website-web#33): switching to one room lowers the party to its limit (10 adults, 10 children; the ages list follows). */
  constructor() {
    this.formGroup.controls.rooms.valueChanges.pipe(takeUntilDestroyed()).subscribe((rooms) => {
      if (rooms !== 1) return;
      const { adults, children } = this.formGroup.controls;
      if ((adults.value ?? 0) > 10) adults.setValue(10);
      if ((children.value ?? 0) > 10) {
        children.setValue(10);
        this.onAddChildren(10);
      }
    });
  }

  //Shortcuts
  get childrenAges() {
    return this.formGroup.controls.childrenAges.controls;
  }
  get hasChildrenAgesError() {
    if (!this.formGroup.controls.childrenAges.controls.length) return false;
    return this.formGroup.controls.childrenAges.controls.some((e) => e.errors);
  }

  get hasLimitErrorOndepartureCheckInAndCheckOutDaysCount() {
    return Boolean(this.formGroup.controls.checkInAndCheckOutDaysCount.errors?.['max']);
  }
  get formStatus() {
    return this.#selectors.formStatus;
  }

  // Countries Selector helpers
  readonly countriesTypedToken!: Country;
  readonly countries = this.#selectors.destinationCountries;
  readonly countriesIconGetter = (c?: Country) => {
    if (c?.countryCode === null) return 'map-marker';
    const icon = c?.countryCode ? (`flag-${c.countryCode.toLowerCase()}` as SVGIcon) : 'map-marker';
    return SVG_ICONS?.[icon] ? icon : 'map-marker';
  };

  //Cities Selectotor helpers
  readonly citiesTypedToken!: City;
  readonly citiesExtraOptions: City[] = [{ cityUID: null as UnsafeValue<string>, cityName: 'All departure cities' }];

  //Locations Selector helpers
  readonly locationsTypedToken!: DistrictsTreeItem;
  readonly locations = this.#selectors.locations;

  //Hotels Selector helpers
  readonly availableHotelsTypedToken!: Hotel;
  get availableHotels() {
    return this.#selectors.hotels;
  }
  readonly hotelsOptionFilterPredicate = signal<Predicate<Hotel> | null>(null);
  /** How many ticked hotels the last City / stars / Categories change unticked (R-SRF7); 0 = no message. */
  readonly droppedHotelsCount = signal(0);
  /** The translation key (= the English text) of the message under the Hotels box; `{{count}}` is filled by the template. */
  readonly droppedHotelsMessageKey = 'Hotels unticked because they no longer match the filter: {count}';

  //Currency Selector helpers
  get activedCurrency() {
    return this.#selectors.activedCurrency;
  }
  get currencies() {
    return this.#selectors.currencies;
  }
  get onCurrencySelect() {
    return this.actions.setCurrency;
  }

  //Misc Selector Options
  readonly mealPlansOptions = STATIC_OPTIONS_HOTEL_MEAL_PLANS;
  readonly hotelClassesOptions = STATIC_OPTIONS_HOTEL_CLASSES_STARS;
  readonly hotelServicesOptions = STATIC_OPTONS_HOTEL_SERVICES;
  readonly hotelTypesOptions = STATIC_OPTONS_HOTEL_TYPES;

  //Utils
  periodCheckInDateToFilterFn = (d: Date | null): boolean => {
    const checkInDate = this.formGroup.controls.checkInDate.value;
    if (checkInDate && d) {
      const diff = differenceInDays(d, checkInDate);
      if (diff >= 0 && diff <= 7) {
        return true;
      }
    }
    return false;
  };
  init() {
    if (this.#isInit) return;
    this.#initSearchData();
    this.#isInit = true;
  }

  /** C17: the country chooser's "Try again" action - re-dispatches the destination countries load with `refresh`. */
  retryDestinationCountries() {
    this.actions.getDestinationCountries(true);
  }

  #observeMandatoryOptions() {
    const mandatoryOptions: Observable<RequestStateModel>[] = [
      toObservable(this.countries, { injector: this.#injector }),
      toObservable(this.locations, { injector: this.#injector }),
      toObservable(this.availableHotels, { injector: this.#injector })
    ];
    return combineLatest(
      mandatoryOptions.map((e) =>
        e.pipe(
          filter((e) => e.loadState.status === 'completed'),
          map((e) => e.loadState.status === 'completed'),
          take(1)
        )
      )
    ).pipe(map((result) => result.every((value) => value)));
  }
  updateHotelFilters(isInitialRequest = false) {
    if (!isInitialRequest && !this.#isReady) return;

    const { hotelClasses, hotelTypes, locations } = this.formGroup.value;

    const cityIds = locations?.filter((el) => el.type === 'city').map((el) => el.value);
    const districtIds = locations?.filter((el) => el.type === 'district').map((el) => el.value);
    if (!hotelClasses && !hotelTypes && !locations && !cityIds && !districtIds) {
      if (this.hotelsOptionFilterPredicate) this.hotelsOptionFilterPredicate.update(() => null);
      if (!isInitialRequest) this.#keepMatchingHotels(null);
      return;
    }

    const predicate = (hotel: Hotel) =>
      (!hotelClasses?.length || hotelClasses.includes(hotel.hotelClass as string)) &&
      (!hotelTypes?.length || hotelTypes.includes(hotel.hotelType as HotelTypeEnum)) &&
      (!cityIds?.length || cityIds.includes(hotel.cityUID)) &&
      (!districtIds?.length || districtIds.includes(hotel.districtUID));
    this.hotelsOptionFilterPredicate.update(() => predicate);
    if (!isInitialRequest) this.#keepMatchingHotels(predicate);
  }

  /**
   * R-SRF7 (website-web#54, Amiran 12.10.2026 "keep matching, drop rest"): after a City / stars / Categories change the ticked hotels that still
   * match the new filter stay ticked, the others are unticked and `droppedHotelsCount` says how many - the search never widens silently.
   */
  #keepMatchingHotels(predicate: Predicate<Hotel> | null) {
    const ticked = this.formGroup.controls.hotelCodes.value ?? [];
    if (!ticked.length) {
      this.droppedHotelsCount.set(0);
      return;
    }
    const known = this.availableHotels().response ?? [];
    const kept = ticked.filter((code) => {
      const hotel = known.find((h) => h.hotelCode === code);
      return !!hotel && (!predicate || predicate(hotel));
    });
    this.formGroup.controls.hotelCodes.setValue(kept.length ? kept : null);
    this.droppedHotelsCount.set(ticked.length - kept.length);
  }

  onAddChildren(count: number) {
    // R-BIH42 (website-web#84): never below 0 - removeAt on an empty array does nothing, so a negative count looped forever and hung the tab
    const children = Math.max(count ?? 0, 0);
    const current = this.formGroup.controls.childrenAges.length;
    if (current < children)
      for (let index = 0; index < children - current; index++)
        this.formGroup.controls.childrenAges.push(new FormControl<number | null>(null, Validators.required));
    else if (current > children)
      // R-HP-R3 (website-web#33): remove from the end - a climbing index over a shrinking array left stale age fields
      while (this.formGroup.controls.childrenAges.length > children)
        this.formGroup.controls.childrenAges.removeAt(this.formGroup.controls.childrenAges.length - 1);
  }
  /** Set by the form component while its `[ngxsForm]` directive is on screen: copies the form into the store at once (R-SRF27). */
  copyFormToStore: (() => void) | null = null;

  onSubmit(showLoader = false) {
    if (!this.isSubmited()) this.isSubmited.update(() => true);
    if (this.formGroup.invalid) return;

    this.pageRowCount.set(25);

    // R-SRF27 (website-web#53): the form plugin copies the form into the store after a 100 ms debounce and the search reads the store -
    // copy it now, so the request carries the form exactly as it is when Search is pressed.
    this.copyFormToStore?.();
    this.actions.searchHotels();

    if (this.#selectors.isGeneratedURL()) this.actions.resultIsFromGeneratedUrl(false);

    if (showLoader) {
      this.#loadingWindowService.show();
      // the window stays until the first offers are listed (or the search completed or failed), not merely until the first answer
      toObservable(this.#selectors.firstBatchReady, { injector: this.#injector })
        .pipe(filter(Boolean), take(1))
        .subscribe(() => this.#loadingWindowService.hide());
    }
  }
  /**
   * Amiran, 23.09.2026: "destination chooser tab disapeared from only hotels why ?" - the form held a country the site no longer offered
   * (the one remembered in the browser, first time the built-in default), so the chooser showed nothing while the page listed that
   * country's cities and hotels. Once the offered countries are in, the form keeps its country only if it is offered; otherwise it takes
   * the API's default, else the first offered (pickDestinationCountry), with that country's selling currency as a manual choice would.
   */
  async #settleDestinationCountry() {
    const offered = await lastValueFrom(
      toObservable(this.countries, { injector: this.#injector }).pipe(
        filter((e) => !!e.loadState.status && e.loadState.status !== 'loading'),
        take(1)
      )
    );
    const current = this.formGroup.controls.countryCode.value;
    const code = pickDestinationCountry(current, offered.response, this.#selectors.destinationDefaultCountry());
    if (!code) return;
    if (code !== current) {
      this.formGroup.controls.countryCode.setValue(code);
      this.#storeCountryCode = code;
      // R-SRF34: a City or hotel picked from the lists read early belongs to the country the form left - reset as onCountryChange does
      if (current) {
        this.formGroup.controls.locations.setValue(null);
        this.formGroup.controls.hotelCodes.setValue(null);
        this.droppedHotelsCount.set(0);
        this.hotelsOptionFilterPredicate.update(() => null);
      }
    }
    // website-web#90: the selling currency is applied also when the form already held this country (a fresh session's default) -
    // returning early left the header listing every currency instead of the destination's own. appSetSellCurrency is a no-op when
    // nothing changed and keeps the agent's own currency while the destination offers it.
    const sellCurrency = offered.response?.find((c) => c.countryCode === code)?.sellCurrency;
    if (sellCurrency) this.actions.setSellCurrency('hotels', sellCurrency);
  }
  async #initSearchData() {
    let searchByUrlData: ReturnType<typeof this.formGroup.getRawValue> | null =
      this.#selectors.searchByUrlData() as ReturnType<typeof this.formGroup.getRawValue>;

    if (searchByUrlData) {
      // R-SRF8b (docs#446): a party outside R-MR7 (one room 10 / 10, Auto or 2-5 rooms 30 / 20) is put back into it, with a warning
      const limits = searchByUrlData.rooms === 1 ? { adults: 10, children: 10 } : { adults: 30, children: 20 };
      const clamped = clampSearchLinkParty(searchByUrlData, {
        minAdults: 1,
        maxAdults: limits.adults,
        maxChildren: limits.children
      });
      searchByUrlData = clamped.payload;
      if (clamped.adjusted) warnSearchLink(this.#injector, SEARCH_LINK_ADJUSTED_MESSAGE);

      if (searchByUrlData.nights || searchByUrlData.nightsTo || searchByUrlData.checkInDateTo)
        this.onDateSearchModeChange('searchByPeriods');
      else this.onDateSearchModeChange('searchByDates');

      if (searchByUrlData.children && !searchByUrlData?.childrenAges?.length)
        this.onAddChildren(searchByUrlData.children);

      this.actions.updateFormValue({ path: this.ngxsForm, value: searchByUrlData });

      this.#isSearchByModule = true;
      this.#loadingWindowService.show();
    }

    this.actions.getDestinationCountries();

    let storedFormData = this.#selectors.searchFormData() as typeof this.formGroup.value;

    // R-HF10 (website-web#45, retest 2): the card draws its tab ONCE, in ngAfterViewInit - the restored mode must be set before the first
    // await below, else a stored "Search by periods" form is shown on the "Search by dates" tab (values were right, the tab was not)
    if (!searchByUrlData && storedFormData?.checkInDate) {
      const hasStoredPeriodFields = [storedFormData.nights, storedFormData.nightsTo, storedFormData.checkInDateTo].some(
        (value) => value !== null && value !== undefined
      );
      this.onDateSearchModeChange(hasStoredPeriodFields ? 'searchByPeriods' : 'searchByDates', true);
    }

    let isIntConvert = false;

    if (
      storedFormData?.countryId ||
      storedFormData?.hotelIds?.length ||
      storedFormData?.locations?.some((e) => e.valueInt)
    ) {
      const convertedParams = await this.#convertIntToCodes({
        countryId: storedFormData.countryId,
        hotelIds: storedFormData.hotelIds,
        locations: storedFormData.locations ?? [],
        // R-HF10: a form restored from the tab names its country by CODE - the City / Hotels lists are read for it
        fallbackCountryCode: storedFormData.countryCode ?? undefined
      });

      storedFormData = {
        ...storedFormData,
        ...convertedParams,
        // Reset Int values
        hotelIds: null,
        countryId: null
      };
      isIntConvert = true;
    }

    if (storedFormData) this.formGroup.patchValue({ ...storedFormData });

    // R-SRF34 (website-web#200): the form already holds a country (kept from the last visit, else the default) - its City and Hotels lists
    // are read now, together with the countries list, not after it (production 03.11.2026: every call < 0.3 s, the boxes waited for the chain)
    let listsReadFor: string | null = null;
    const heldCountryCode = this.formGroup.controls.countryCode.value;
    if (!isIntConvert && heldCountryCode) {
      listsReadFor = heldCountryCode;
      this.actions.getLocations({ countryCode: heldCountryCode }, true);
      this.actions.getHotels({ countryCode: heldCountryCode }, true);
    }

    // 23.09.2026: never open on a country the site does not offer - the cities and hotels below are read for it
    await this.#settleDestinationCountry();

    const formData = this.formGroup.getRawValue();
    if (storedFormData?.childrenAges?.length)
      for (const iterator of storedFormData.childrenAges)
        this.formGroup.controls.childrenAges.push(new FormControl<number | null>(iterator, Validators.required));

    // R-HF9 (website-web#45 retest): the restored country IS the one loaded here - without this the ready check below took it for a
    // country change made by the agent and cleared the restored City / Meal plan / Stars / Type / Services
    this.#storeCountryCode = formData.countryCode ?? this.#storeCountryCode;
    // R-SRF34: read again only when the countries answer moved the form to another country (the call above, still open, is cancelled)
    if (listsReadFor === null || formData.countryCode !== listsReadFor) {
      this.actions.getLocations({ countryCode: formData.countryCode ?? undefined }, true);
      if (!isIntConvert && !storedFormData?.hotelIds?.length)
        this.actions.getHotels({ countryCode: formData.countryCode ?? undefined }, true);
    }

    // R-HF8 (website-web#45): a form restored from the tab keeps its dates and nights - only a first visit starts from the defaults
    const hasPeriodFields = [storedFormData?.nights, storedFormData?.nightsTo, storedFormData?.checkInDateTo].some(
      (value) => value !== null && value !== undefined
    );
    let restoredMode: SearchDateModes | null = null;
    if (storedFormData?.checkInDate) restoredMode = hasPeriodFields ? 'searchByPeriods' : 'searchByDates';
    this.onDateSearchModeChange(
      this.searchMode() ?? restoredMode ?? 'searchByDates',
      !this.searchMode() && restoredMode !== null
    );

    this.#observeMandatoryOptions().subscribe((isReady) => {
      this.#isReady = isReady;
      if (!isReady) return;

      // //update hotel filters
      this.calcCheckInAndCheckOutDaysCount();
      this.updateHotelFilters(true);

      // Recover a country change made before the form was ready. The country selector is interactive
      // as soon as the small countries list loads (~1s before the large hotels list marks the form
      // ready), so a selection made in that window is dropped by the `if (!this.#isReady)` guard in
      // onCountryChange — the dropdown shows the new country while locations/hotels stay on the one
      // loaded at init. If they no longer match, reload for the selected country now (a real Country
      // object, so the null/popup branch is never taken).
      const selectedCountry = this.formGroup.controls.countryCode.value;
      if (selectedCountry && selectedCountry !== this.#storeCountryCode) {
        const country = this.#selectors.destinationCountries().response?.find((c) => c.countryCode === selectedCountry);
        if (country) this.onCountryChange(country);
      }

      //Submit while SearchByUrl module exists
      if (this.#isSearchByModule) {
        if (this.formGroup.controls.searchCurrency.value) {
          const currencyExists = this.#selectors
            .currencies()
            .some((e) => e.code === this.formGroup.controls.searchCurrency.value);
          if (currencyExists) this.actions.setCurrency(this.formGroup.controls.searchCurrency.value);
        }

        setTimeout(async () => {
          if (searchByUrlData) await this.#validateSearchByUrlPayload(searchByUrlData);

          this.#isSearchByModule = false;
          this.actions.clearSearchByUrl();
          this.onSubmit(true);
          this.actions.resultIsFromGeneratedUrl(true);
        }, 1000);
      }
    });
  }

  async #validateSearchByUrlPayload(payload: ReturnType<typeof this.formGroup.getRawValue>) {
    const undefinedLog: { prop: string; note: string; ovverridden: unknown }[] = [];
    const formGroup = this.formGroup;
    const requiredFields: {
      field: keyof ReturnType<typeof formGroup.getRawValue> | (keyof ReturnType<typeof formGroup.getRawValue>)[];
      defaultField?: keyof ReturnType<typeof formGroup.getRawValue>;
    }[] = [];
    const keysToIgnore: (keyof ReturnType<typeof formGroup.getRawValue>)[] = ['checkInAndCheckOutDaysCount'];

    // Helper for undefined check
    const checkUndefined = (
      fields: (keyof ReturnType<typeof formGroup.getRawValue>)[] | keyof ReturnType<typeof formGroup.getRawValue>,
      defaultField?: keyof ReturnType<typeof formGroup.getRawValue>
    ) => {
      if (typeof fields === 'string') {
        if (typeof payload[fields] === 'undefined') {
          undefinedLog.push({
            prop: fields,
            note: 'is mandatory',
            ovverridden: formGroup.controls[fields]?.value
          });
        }
      } else if (defaultField && fields.every((f) => typeof payload[f] === 'undefined')) {
        undefinedLog.push({
          prop: fields.join(' or '),
          note: 'is mandatory, used: ' + defaultField,
          ovverridden: formGroup.controls[defaultField]?.value
        });
      }
    };
    requiredFields.push({ field: ['countryCode', 'countryId'], defaultField: 'countryCode' }, { field: 'checkInDate' });
    const wrongParams: string[] = [];

    if (this.searchMode() === 'searchByDates') {
      keysToIgnore.push('nights', 'nightsTo', 'checkInDateTo');
      requiredFields.push({ field: 'checkOutDate' });
    } else {
      keysToIgnore.push('checkOutDate');

      requiredFields.push({ field: 'nights' }, { field: 'nightsTo' }, { field: 'checkInDateTo' });
    }
    requiredFields.forEach(({ field, defaultField }) => {
      checkUndefined(field, defaultField);
    });

    // Unknown keys check
    const formValues = formGroup.getRawValue();
    const formKeys = Object.keys(formValues).filter(
      (e) => !keysToIgnore.includes(e as keyof ReturnType<typeof formGroup.getRawValue>)
    );
    const unknownKeys = Object.keys(payload).filter((key) => !formKeys.includes(key));

    if (unknownKeys.length || undefinedLog.length || wrongParams.length || this.formGroup.invalid) {
      console.info(
        '%cNote: These messages can be ignored unless troubleshooting search payload issues.',
        'color: #2ecc71; font-size: 14px; font-weight: bold;'
      );

      console.groupCollapsed(
        '%cSearch Payload Validation Issues (%s Mode)',
        'color: #e67e22; font-size: 15px; font-weight: bold;',
        this.searchMode()
      );
      console.info(
        'Detected issues in the search payload may affect the results, as default values will be used for some properties.\n\n' +
          'Search mode depends on the following properties:  "nights", "nightsTo", and "checkInDateTo". ' +
          'If any of these are present, the system will use "searchByPeriods"; otherwise, it will use "searchByDates".\n\n' +
          'Also, please note that the system uses strings (UID or Code) instead of integers. The following properties will be automatically converted to the correct string-based format:\n' +
          '\t- "hotelIds" → "hotelCodes"\n' +
          '\t- "countryId" → "countryCode"\n' +
          '\t- "locations.valueInt" → "locations.value"\n\n' +
          'This will not affect the search results.'
      );
      if (unknownKeys.length) {
        console.group('%cUnsupported properties in payload:', 'color:#FF0000; font-size: 14px;');
        console.table(unknownKeys);
        console.groupEnd();
        console.groupCollapsed('Supported properties:');
        console.table(formKeys);
        console.groupEnd();
      }

      if (undefinedLog.length) {
        console.group('%cMissing or undefined properties:', 'color:#FF0000; font-size: 14px;');
        console.table(undefinedLog);
        console.groupEnd();
      }
      if (wrongParams.length) {
        console.group('%cIncompatible properties for current mode:', 'color:#da9c55; font-size: 14px;');
        wrongParams.forEach((e) => console.info(`%c${e}`, 'color:#FF0000; font-size: 14px;'));
        console.groupEnd();
      }

      // log required params for this search
      console.groupCollapsed('Required properties for this search:');
      const mandatoryLog: Record<string, string> = {};
      requiredFields.forEach((e) => {
        if (typeof e.field === 'string') {
          mandatoryLog[e.field] = typeof formValues[e.field];
        } else {
          e.field.forEach((f) => {
            mandatoryLog[f] = typeof formValues[f];
          });
        }
      });
      console.table(mandatoryLog);
      console.groupEnd();

      const currentPayload = deepClean(formValues);

      console.groupCollapsed('Properties used for search:');
      console.table(currentPayload);
      console.groupEnd();

      this.#loadingWindowService.hide();

      const message = this.formGroup.invalid
        ? 'The search form contains invalid fields. Please review all inputs.'
        : 'The search payload has missing or incorrect parameters. That may affect the search results.';
      const confirmation = this.#confirmService.confirm(
        {
          title: 'Warning',
          message
        },
        'warn',
        { primaryButton: { label: 'Continue' } }
      );
      console.groupEnd();

      if (!this.formGroup.invalid) {
        await lastValueFrom(confirmation);
        this.#loadingWindowService.show();
      }
    }
  }
  async #convertIntToCodes({
    countryId,
    hotelIds,
    locations,
    fallbackCountryCode
  }: Pick<SearchOnlyHotelPackagesRequest, 'countryId' | 'hotelIds' | 'locations'> & {
    fallbackCountryCode?: string;
  }): Promise<Pick<SearchOnlyHotelPackagesRequest, 'hotelCodes' | 'countryCode' | 'locations'>> {
    const result: Pick<SearchOnlyHotelPackagesRequest, 'hotelCodes' | 'countryCode' | 'locations'> = {};
    //Convert countryId to countryCode
    if (countryId) {
      const data = await lastValueFrom(
        toObservable(this.countries, { injector: this.#injector }).pipe(
          filter((e) => e.loadState.status === 'completed'),
          take(1)
        )
      );

      const country = data.response?.find((e) => e.countryID === countryId);

      if (country?.countryCode) {
        result['countryCode'] = country.countryCode;
      }
    }

    const listCountryCode = result['countryCode'] ?? fallbackCountryCode;
    this.actions.getHotels({ countryCode: listCountryCode }, true);

    //Convert HotelIds to Codes
    if (hotelIds?.length) {
      const hotelsData = await lastValueFrom(
        toObservable(this.availableHotels, { injector: this.#injector }).pipe(
          filter((e) => e.loadState.status === 'completed'),
          take(1)
        )
      );
      if (hotelsData?.response?.length) {
        const hotelCodes: string[] = [];
        hotelIds.forEach((id) => {
          const hotelCode = hotelsData?.response?.find((e) => e.hotelID === id)?.hotelCode;
          if (hotelCode) hotelCodes.push(hotelCode);
        });

        result['hotelCodes'] = hotelCodes;
      }
    }

    //Convert Locations Int values to Values
    const hasIntValues = locations?.some((e) => e.valueInt && e.valueInt > 0);
    if (hasIntValues) {
      this.actions.getLocations({ countryCode: listCountryCode }, true);
      const locationsData = await lastValueFrom(
        toObservable(this.locations, { injector: this.#injector }).pipe(
          filter((e) => e.loadState.status === 'completed'),
          take(1)
        )
      );

      const _locations = convertLocationsIntValuesToValues(locations ?? [], locationsData.response ?? []);

      result['locations'] = _locations;
    }

    return result;
  }
  resetForm() {
    this.formGroup.reset({
      adults: 2,
      rooms: 0,
      children: 0,
      childrenAges: [],
      countryCode: this.#selectors.activedDestinationCountry(),
      checkInDate: this.today,
      checkOutDate: addDays(this.today, 1),
      checkInAndCheckOutDaysCount: 1
    });
    this.droppedHotelsCount.set(0);
    this.actions.updateFormValue({ path: this.ngxsForm, value: this.formGroup.getRawValue() });
    setTimeout(() => this.actions.resetSearchResult(), 100);
    if (this.isSubmited()) this.isSubmited.update(() => false);
  }
  onCountryChange(country: Country | null) {
    if (!this.#isReady) return;
    let countryCode = country?.countryCode;

    if (!countryCode) {
      // R-HL1 (website-web#34, Amiran 09.10.2026): no outbound link from the Hotels form - a cleared country keeps the last one
      countryCode = this.#storeCountryCode ?? this.#selectors.activedDestinationCountry();
      this.formGroup.controls.countryCode.setValue(countryCode);
      return;
    }
    if (!country?.sellCurrency) return;
    this.actions.setSellCurrency('hotels', country.sellCurrency);
    if (this.#storeCountryCode === countryCode) return;

    this.#storeCountryCode = countryCode ?? null;

    //reset selected cities & hotels

    this.formGroup.controls.locations.setValue(null);
    this.formGroup.controls.hotelCodes.setValue(null);
    this.droppedHotelsCount.set(0);
    this.hotelsOptionFilterPredicate.update(() => null);

    this.actions.getLocations({ countryCode }, true);
    this.actions.getHotels({ countryCode }, true);

    this.#clearSearchFormFilterControls();
  }
  #clearSearchFormFilterControls() {
    this.formGroup.patchValue({
      mealPlans: null,
      hotelClasses: null,
      hotelTypes: null,
      hotelServices: null
    });
  }

  calcCheckInAndCheckOutDaysCount(isCheckInDateChange = false) {
    const checkInDate = this.formGroup.controls.checkInDate.value;
    let checkOutDate = this.formGroup.controls.checkOutDate.value;
    const checkInAndCheckOutDaysCount = this.formGroup.controls.checkInAndCheckOutDaysCount.value;

    if (
      checkInDate &&
      isCheckInDateChange &&
      checkInAndCheckOutDaysCount !== null &&
      checkInAndCheckOutDaysCount > -1
    ) {
      checkOutDate = addDays(checkInDate, checkInAndCheckOutDaysCount);
      this.formGroup.controls.checkOutDate.setValue(checkOutDate);
    } else {
      const difference = checkInDate && checkOutDate ? differenceInDays(checkOutDate, checkInDate) : null;
      if (difference && difference < 0 && checkInDate) {
        // R-SL11: a Check Out typed before the Check In is put back as Check In + the Nights the form had (7 when it had none)
        const keptNights =
          checkInAndCheckOutDaysCount && checkInAndCheckOutDaysCount > 0 ? checkInAndCheckOutDaysCount : 7;
        checkOutDate = addDays(checkInDate, keptNights);
        this.formGroup.controls.checkOutDate.setValue(checkOutDate);
      }
      this.formGroup.controls.checkInAndCheckOutDaysCount.setValue(
        checkInDate && checkOutDate ? differenceInDays(checkOutDate, checkInDate) : null
      );
    }
  }

  updateCheckInAndCheckOutDaysCount() {
    const checkInAndCheckOutDaysCount = this.formGroup.controls.checkInAndCheckOutDaysCount.value;
    if (checkInAndCheckOutDaysCount === null || checkInAndCheckOutDaysCount < 0) return;

    let checkInDate = this.formGroup.controls.checkInDate.value;
    if (!checkInDate) {
      checkInDate = new Date();
      this.formGroup.controls.checkInDate.setValue(checkInDate);
    }

    this.formGroup.controls.checkOutDate.setValue(addDays(checkInDate, checkInAndCheckOutDaysCount));
  }

  openInstructions() {
    this.#dynamicModalService.open({
      title: 'Instructions for booking hotels',
      component: HotelBookingInstructionsComponent,
      styleClass: 'w-[40rem]'
    });
  }

  /** `keepValues`: the mode only switches validators - the dates / nights already in the form (restored after a reload) stay. */
  onDateSearchModeChange(mode: SearchDateModes | null, keepValues = false) {
    if (this.searchMode() === mode) return;
    this.searchMode.update(() => mode);
    if (mode === 'searchByDates') {
      if (!keepValues) {
        this.formGroup.controls.nights.setValue(null);
        this.formGroup.controls.nightsTo.setValue(null);
        this.formGroup.controls.checkInDateTo.setValue(null);

        if (!this.formGroup.controls.checkInDate.value) this.formGroup.controls.checkInDate.setValue(this.today);

        this.formGroup.controls.checkOutDate.setValue(addDays(this.formGroup.controls.checkInDate.value!, 1));
      }

      // Enable Validators
      this.formGroup.controls.checkOutDate.addValidators(Validators.required);
      this.formGroup.controls.checkOutDate.updateValueAndValidity();
      this.formGroup.controls.checkInAndCheckOutDaysCount.addValidators(Validators.required);
      this.formGroup.controls.checkInAndCheckOutDaysCount.updateValueAndValidity();
      this.formGroup.controls.checkInAndCheckOutDaysCount.enable();

      // Remove Validators
      this.formGroup.controls.checkInDateTo.removeValidators(Validators.required);
      this.formGroup.controls.checkInDateTo.updateValueAndValidity();
      //
      this.formGroup.updateValueAndValidity();
      this.calcCheckInAndCheckOutDaysCount();
    } else {
      if (!keepValues) {
        this.formGroup.controls.checkInDate.setValue(this.today);

        this.formGroup.controls.checkInDateTo.setValue(this.today);

        this.formGroup.controls.nights.setValue(2);
        this.formGroup.controls.nightsTo.setValue(2);
        //
        this.formGroup.controls.checkOutDate.setValue(null);
        this.formGroup.controls.checkInAndCheckOutDaysCount.setValue(null);
      }

      // Enable Validators
      this.formGroup.controls.checkInDateTo.addValidators(Validators.required);
      this.formGroup.controls.checkInDateTo.updateValueAndValidity();
      // Remove Validators
      this.formGroup.controls.checkOutDate.removeValidators(Validators.required);
      this.formGroup.controls.checkOutDate.updateValueAndValidity();
      this.formGroup.controls.checkInAndCheckOutDaysCount.removeValidators(Validators.required);
      this.formGroup.controls.checkInAndCheckOutDaysCount.updateValueAndValidity();
      this.formGroup.controls.checkInAndCheckOutDaysCount.disable();
      this.formGroup.updateValueAndValidity();
    }
  }

  updateCheckInDateTo() {
    const checkInDate = this.formGroup.controls.checkInDate.value;
    const checkInDateTo = this.formGroup.controls.checkInDateTo.value;
    if (!checkInDate || !checkInDateTo) {
      return;
    }
    const diff = differenceInDays(checkInDateTo, checkInDate);
    if (diff < 1 || diff > 7) this.formGroup.controls.checkInDateTo.setValue(checkInDate);
  }

  onNightsChange(change: 'nights' | 'nightsTo') {
    const nights = this.formGroup.controls.nights.value ?? 0;
    const nightsTo = this.formGroup.controls.nightsTo.value ?? 0;
    if (change === 'nights' && nights > nightsTo) this.formGroup.controls.nightsTo.setValue(nights);
    else if (change === 'nightsTo' && nightsTo < nights) this.formGroup.controls.nights.setValue(nightsTo);
  }
}
